"use client"

import React from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Switch } from "../../../components/ui/switch"
import { Separator } from "../../../components/ui/separator"
import { Button } from "../../../components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select"
import { SliderField, Vec3Field } from "./control-fields"
import type { EasingName, Take } from "./take-types"
import { Camera, Navigation  } from "lucide-react"

// Funções de easing para preview visual
const EASING_FNS: Record<string, (t: number) => number> = {
  linear:          t => t,
  easeIn:          t => t * t,
  easeOut:         t => t * (2 - t),
  easeInOut:       t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
  easeInCubic:     t => t * t * t,
  easeOutCubic:    t => --t * t * t + 1,
  easeInOutCubic:  t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
  easeInOutQuart:  t => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t,
  easeInBack:      t => t * t * (2.7 * t - 1.7),
  easeOutBack:     t => { const c = 1.70158; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2) },
  easeInOutBack:   t => { const c = 1.70158 * 1.525; return t < 0.5 ? (Math.pow(2*t,2)*((c+1)*2*t-c))/2 : (Math.pow(2*t-2,2)*((c+1)*(2*t-2)+c)+2)/2 },
  easeInElastic:   t => t === 0 ? 0 : t === 1 ? 1 : -Math.pow(2, 10*t-10) * Math.sin((t*10-10.75) * (2*Math.PI/3)),
  easeOutElastic:  t => t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10*t) * Math.sin((t*10-0.75) * (2*Math.PI/3)) + 1,
  easeOutBounce:   t => { if(t<1/2.75) return 7.5625*t*t; if(t<2/2.75){t-=1.5/2.75;return 7.5625*t*t+0.75}if(t<2.5/2.75){t-=2.25/2.75;return 7.5625*t*t+0.9375}t-=2.625/2.75;return 7.5625*t*t+0.984375 },
}

const EASING_GROUPS = [
  { label: "Básico",   values: ["linear","easeIn","easeOut","easeInOut"] },
  { label: "Cúbico",   values: ["easeInCubic","easeOutCubic","easeInOutCubic","easeInOutQuart"] },
  { label: "Especial", values: ["easeInBack","easeOutBack","easeInOutBack","easeInElastic","easeOutElastic","easeOutBounce"] },
]

const EASINGS: EasingName[] = Object.keys(EASING_FNS) as EasingName[]

interface TakeEditorProps {
  take: Take
  onChange: (take: Take) => void
  onCaptureStart: () => void
  onCaptureEnd: () => void
  onGoToStart?: () => void   // move câmera para posição inicial do take
  onGoToEnd?:   () => void   // move câmera para posição final do take
}


// Mini curva SVG para visualizar o easing
function EasingCurve({ name, size = 32 }: { name: string; size?: number }) {
  const fn = EASING_FNS[name] ?? EASING_FNS.linear
  const steps = 30
  const pts = Array.from({ length: steps + 1 }, (_, i) => {
    const t = i / steps
    const v = fn(Math.max(0, Math.min(1, t)))
    return `${t * size},${(1 - v) * size}`
  }).join(' ')
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block', flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke="#6366f1" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

// Dropdown customizado com preview de curva
function EasingSelect({ value, onChange }: { value: EasingName; onChange: (v: EasingName) => void }) {
  const [open, setOpen] = React.useState(false)
  return (
    <div style={{ position: 'relative' }}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '6px 10px', background: 'hsl(var(--background))',
          border: '1px solid hsl(var(--border))', borderRadius: 6,
          cursor: 'pointer', justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <EasingCurve name={value} size={24} />
          <span style={{ fontSize: 13, color: 'hsl(var(--foreground))' }}>{value}</span>
        </div>
        <svg width="12" height="12" viewBox="0 0 12 12" style={{ opacity: 0.5 }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '105%', left: 0, right: 0, zIndex: 50,
          background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))',
          borderRadius: 8, boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
          overflow: 'hidden', maxHeight: 320, overflowY: 'auto',
        }}>
          {EASING_GROUPS.map(group => (
            <div key={group.label}>
              <div style={{ padding: '6px 10px 3px', fontSize: 10, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid hsl(var(--border))' }}>
                {group.label}
              </div>
              {group.values.map(name => (
                <button
                  key={name}
                  type="button"
                  onClick={() => { onChange(name as EasingName); setOpen(false) }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '7px 10px', background: name === value ? 'hsl(var(--accent))' : 'transparent',
                    border: 'none', cursor: 'pointer',
                    borderBottom: '1px solid hsl(var(--border) / 0.5)',
                  }}
                >
                  <EasingCurve name={name} size={28} />
                  <span style={{ fontSize: 12, color: 'hsl(var(--foreground))' }}>{name}</span>
                  {name === value && (
                    <svg width="12" height="12" viewBox="0 0 12 12" style={{ marginLeft: 'auto', color: '#6366f1' }}>
                      <path d="M2 6l3 3 5-5" stroke="#6366f1" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function TakeEditor({ take, onChange, onCaptureStart, onCaptureEnd, onGoToStart, onGoToEnd }: TakeEditorProps) {
  if (!take) return null
  const update = (patch: Partial<Take>) => onChange({ ...take, ...patch })

  return (
    <div className="space-y-4">
      {/* Nome */}
      <div className="space-y-2">
        <Label className="text-xs font-medium">Nome do take</Label>
        <Input
          value={take.name}
          onChange={(e) => update({ name: e.target.value })}
          className="h-9"
        />
      </div>

      <Tabs defaultValue="tempo" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="tempo">Tempo</TabsTrigger>
          <TabsTrigger value="camera">Câmera</TabsTrigger>
          <TabsTrigger value="luz">Luz</TabsTrigger>
        </TabsList>

        {/* TEMPO */}
        <TabsContent value="tempo" className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Início (s)</Label>
              <Input
                type="number"
                step={0.5}
                value={take.startTime}
                onChange={(e) => update({ startTime: Number.parseFloat(e.target.value) || 0 })}
                className="h-8 font-mono text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Fim (s)</Label>
              <Input
                type="number"
                step={0.5}
                value={take.endTime}
                onChange={(e) => update({ endTime: Number.parseFloat(e.target.value) || 0 })}
                className="h-8 font-mono text-xs"
              />
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Duração: {(take.endTime - take.startTime).toFixed(1)}s
          </div>

          <Separator />

          <div className="space-y-2">
            <Label className="text-xs font-medium">Curva de animação (easing)</Label>
            <EasingSelect value={take.easing} onChange={(v) => update({ easing: v })} />
          </div>

          <div className="rounded-md border border-border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-medium">Fade ao entrar</Label>
                <p className="text-[10px] text-muted-foreground">Transição em preto no início do take</p>
              </div>
              <Switch checked={take.fadeIn} onCheckedChange={(v) => update({ fadeIn: v })} />
            </div>
            {take.fadeIn && (
              <SliderField
                label="Duração do fade (s)"
                value={take.fadeDuration ?? 0.5}
                min={0.1}
                max={2}
                step={0.05}
                unit="s"
                onChange={(v) => update({ fadeDuration: v })}
              />
            )}
          </div>
        </TabsContent>

        {/* CÂMERA */}
        <TabsContent value="camera" className="space-y-4 pt-4">
          <div className="space-y-3 rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold">Posição inicial</Label>
              <div className="flex gap-1.5">
                {onGoToStart && (
                  <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={onGoToStart} title="Mover câmera para esta posição">
                    <Navigation className="size-3" />
                    Ir
                  </Button>
                )}
                <Button size="sm" variant="secondary" className="h-7 gap-1 text-[11px]" onClick={onCaptureStart} title="Capturar posição atual da câmera">
                  <Camera className="size-3" />
                  Capturar
                </Button>
              </div>
            </div>
            <Vec3Field
              label="Posição"
              value={take.camera.start.position}
              onChange={(v) =>
                update({ camera: { ...take.camera, start: { ...take.camera.start, position: v } } })
              }
            />
            <Vec3Field
              label="Olhar para (lookAt)"
              value={take.camera.start.lookAt}
              onChange={(v) =>
                update({ camera: { ...take.camera, start: { ...take.camera.start, lookAt: v } } })
              }
            />
            <SliderField
              label="Zoom (FOV inicial)"
              value={take.camera.fovStart}
              min={10}
              max={90}
              step={1}
              unit="°"
              onChange={(v) => update({ camera: { ...take.camera, fovStart: v } })}
            />
          </div>

          <div className="space-y-3 rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold">Posição final</Label>
              <div className="flex gap-1.5">
                {onGoToEnd && (
                  <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={onGoToEnd} title="Mover câmera para esta posição">
                    <Navigation className="size-3" />
                    Ir
                  </Button>
                )}
                <Button size="sm" variant="secondary" className="h-7 gap-1 text-[11px]" onClick={onCaptureEnd} title="Capturar posição atual da câmera">
                  <Camera className="size-3" />
                  Capturar
                </Button>
              </div>
            </div>
            <Vec3Field
              label="Posição"
              value={take.camera.end.position}
              onChange={(v) =>
                update({ camera: { ...take.camera, end: { ...take.camera.end, position: v } } })
              }
            />
            <Vec3Field
              label="Olhar para (lookAt)"
              value={take.camera.end.lookAt}
              onChange={(v) =>
                update({ camera: { ...take.camera, end: { ...take.camera.end, lookAt: v } } })
              }
            />
            <SliderField
              label="Zoom (FOV final)"
              value={take.camera.fovEnd}
              min={10}
              max={90}
              step={1}
              unit="°"
              onChange={(v) => update({ camera: { ...take.camera, fovEnd: v } })}
            />
          </div>
        </TabsContent>

        {/* LUZ */}
        <TabsContent value="luz" className="space-y-4 pt-4">
          <SliderField
            label="Intensidade da luz principal"
            value={take.lighting.intensity}
            min={0}
            max={3}
            step={0.05}
            onChange={(v) => update({ lighting: { ...take.lighting, intensity: v } })}
          />
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Cor da luz</Label>
            <input
              type="color"
              value={take.lighting.color}
              onChange={(e) => update({ lighting: { ...take.lighting, color: e.target.value } })}
              className="h-8 w-14 cursor-pointer rounded border border-border bg-transparent"
              aria-label="Cor da luz"
            />
          </div>
          <SliderField
            label="Luz ambiente"
            value={take.lighting.ambientIntensity}
            min={0}
            max={1.5}
            step={0.05}
            onChange={(v) => update({ lighting: { ...take.lighting, ambientIntensity: v } })}
          />
          <SliderField
            label="Exposição (tonemapping)"
            value={take.lighting.exposure}
            min={0}
            max={2}
            step={0.05}
            onChange={(v) => update({ lighting: { ...take.lighting, exposure: v } })}
          />

          <Separator />

          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div className="space-y-0.5">
              <Label className="text-xs font-medium">Efeito piscar (blink)</Label>
              <p className="text-[10px] text-muted-foreground">Pisca a luz principal</p>
            </div>
            <Switch
              checked={take.lighting.blink.enabled}
              onCheckedChange={(v) =>
                update({ lighting: { ...take.lighting, blink: { ...take.lighting.blink, enabled: v } } })
              }
            />
          </div>

          {take.lighting.blink.enabled && (
            <div className="space-y-3 rounded-md border border-border p-3">
              <SliderField
                label="Início do piscar (s)"
                value={take.lighting.blink.startOffset}
                min={0}
                max={Math.max(1, take.endTime - take.startTime)}
                step={0.1}
                onChange={(v) =>
                  update({ lighting: { ...take.lighting, blink: { ...take.lighting.blink, startOffset: v } } })
                }
              />
              <SliderField
                label="Duração (s)"
                value={take.lighting.blink.duration}
                min={0.1}
                max={Math.max(1, take.endTime - take.startTime)}
                step={0.1}
                onChange={(v) =>
                  update({ lighting: { ...take.lighting, blink: { ...take.lighting.blink, duration: v } } })
                }
              />
              <SliderField
                label="Intervalo (s)"
                value={take.lighting.blink.interval}
                min={0.02}
                max={0.5}
                step={0.01}
                onChange={(v) =>
                  update({ lighting: { ...take.lighting, blink: { ...take.lighting.blink, interval: v } } })
                }
              />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}